import {
  Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation,
} from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { SharedModule } from '../../../../shared/shared.module';
import { ButtonComponent } from '../../../../ui/button/button.component';
import { FormFieldComponent } from '../../../../ui/form-field/form-field.component';
import { SvgIconComponent } from '../../../../ui/svg-icon/svg-icon.component';
import { CustomersService } from '../../../customers/services/customers.services';
import { AddNewLocationPayload, ICountry, ICustomer, IState } from '../../../customers/models/customers.models';
import { IPageCount } from '../../../../shared/models/page.model';

declare var google: any;

/**
 * Presentational form to create a new shipping location inline.
 *
 * Used as step 3 of {@link CompaniesSelectionDialogComponent} and reusable standalone
 * (e.g. future "missing location" flow) — it only collects the fields and emits the
 * `add-new-location` payload; the host owns the actual API call and footer buttons.
 */
@Component({
  selector: 'app-create-location-form',
  standalone: true,
  imports: [SharedModule, ButtonComponent, FormFieldComponent, SvgIconComponent],
  templateUrl: './create-location-form.component.html',
  styleUrls: ['./create-location-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CreateLocationFormComponent implements OnInit, OnDestroy {
  /** Pre-selected "Parent Company" (defaults to the billing company chosen in step 1). */
  @Input() defaultCompany: ICustomer | null = null;
  /** Optional pre-filled address (used by the missing-location flow). Name is never prefilled. */
  @Input() prefill?: { apartment?: string };
  /** Hide the "will not be synced with your ERP" note. */
  @Input() hideErpNote = false;
  /** When set, forwarded into the emitted payload so the location attaches to the order. */
  @Input() orderId?: number;

  @Output() create = new EventEmitter<AddNewLocationPayload>();
  @Output() back = new EventEmitter<void>();

  parentCompany: ICustomer | null = null;
  locationName = '';
  apartment = '';
  // Auto-filled from Google Places when the user picks a suggestion on the
  // Address field (addressAutoComplete); still user-editable afterwards.
  street = '';
  city = '';
  zip = '';
  /** Not auto-filled by Google Places (no distinct component for it) — manual entry only. */
  street2 = '';

  // Parent-company searchable picker
  showCompanyPicker = false;
  companySearchTerm = '';
  companies: ICustomer[] = [];
  isLoadingCompanies = false;
  isLoadingMoreCompanies = false;
  canLoadMoreCompanies = false;

  // Country searchable picker -- auto-selected from a Google address search
  // (matched by ISO code, locale-independent) or picked manually.
  showCountryPicker = false;
  countrySearchTerm = '';
  countryOptions: ICountry[] = [];
  isLoadingCountries = false;
  selectedCountry: ICountry | null = null;
  private countrySearch$ = new Subject<string>();

  // Province/State searchable picker -- disabled and empty until a country
  // is known (either auto-selected or picked manually), then scoped to it.
  showProvincePicker = false;
  /** Kept even without a matching `res.country.state` record (e.g. France
   * ships none in base Odoo) so the parsed region text is never silently
   * dropped -- sent as the plain-text `province` fallback on submit. */
  provinceSearchTerm = '';
  provinceOptions: IState[] = [];
  isLoadingProvinces = false;
  selectedProvince: IState | null = null;
  private provinceSearch$ = new Subject<string>();

  @ViewChild('addressField', { read: ElementRef }) addressFieldRef?: ElementRef<HTMLElement>;
  private autocomplete: any = null;

  private companyPage = 1;
  private readonly companyPageSize = 20;
  private readonly scrollThreshold = 0.8;
  private companySearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private customersService: CustomersService,
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.parentCompany = this.defaultCompany;
    // Location name is intentionally left empty and must be entered manually (required).
    if (this.prefill?.apartment) this.apartment = this.prefill.apartment;

    this.companySearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.companyPage = 1;
        this.loadCompanies(term, false);
      });

    this.countrySearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => this.loadCountries(term));

    this.provinceSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => this.loadProvinces(term));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.searchTimeoutHandle !== null) clearTimeout(this.searchTimeoutHandle);
    if (this.autocomplete) google.maps.event.clearInstanceListeners(this.autocomplete);
  }

  get valid(): boolean {
    return !!this.parentCompany?.id && !!this.locationName.trim() && !!this.apartment.trim();
  }

  toggleCompanyPicker(): void {
    this.showCompanyPicker = !this.showCompanyPicker;
    if (this.showCompanyPicker && this.companies.length === 0) {
      this.loadCompanies('', false);
    }
  }

  onCompanySearch(term: string): void {
    this.companySearchTerm = term;
    this.companySearch$.next(term);
  }

  selectCompany(company: ICustomer): void {
    this.parentCompany = company;
    this.showCompanyPicker = false;
  }

  clearCompany(event: MouseEvent): void {
    event.stopPropagation();
    this.parentCompany = null;
  }

  onCompanyScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const ratio = (target.scrollTop + target.clientHeight) / target.scrollHeight;
    if (!this.isLoadingMoreCompanies && this.canLoadMoreCompanies && ratio > this.scrollThreshold) {
      this.companyPage++;
      this.loadCompanies(this.companySearchTerm, true);
    }
  }

  submit(): void {
    if (!this.valid) return;
    this.create.emit({
      id_company: this.parentCompany!.id!,
      name: this.locationName.trim(),
      apartment: this.apartment.trim(),
      ...(this.street.trim() ? { street: this.street.trim() } : {}),
      ...(this.city.trim() ? { city: this.city.trim() } : {}),
      ...(this.zip.trim() ? { zip: this.zip.trim() } : {}),
      ...(this.street2.trim() ? { street2: this.street2.trim() } : {}),
      // Plain-text fallback -- kept even when `selectedProvince` didn't
      // resolve to a real record (e.g. no res.country.state data for the
      // country at all), so the parsed region is never silently dropped.
      ...(this.provinceSearchTerm.trim() ? { province: this.provinceSearchTerm.trim() } : {}),
      ...(this.selectedCountry ? { country: { id: this.selectedCountry.id, name: this.selectedCountry.name } } : {}),
      ...(this.selectedProvince ? { state: { id: this.selectedProvince.id, name: this.selectedProvince.name } } : {}),
      ...(this.orderId ? { is_main: false, is_new: true, order_id: this.orderId } : {}),
    });
  }

  /** True once the real Google Maps JS SDK (with the Places library) has
   * loaded -- i.e. `index.html`'s script tag has a real, working API key.
   * Drives the dev-only "simulate a selection" fallback below: it
   * disappears on its own the moment a real key is wired up, no
   * environment flag needed. */
  get googlePlacesAvailable(): boolean {
    return typeof google !== 'undefined' && !!google?.maps?.places?.Autocomplete;
  }

  /**
   * Wires Google Places Autocomplete onto the Address field's native input
   * (same pattern as form-warehouse.component.ts / add-customers /
   * add-locations) so picking a suggestion auto-populates
   * street/city/zip/province/country below — still user-editable
   * afterwards. Reused as-is: this fills exactly the structured fields
   * `LocationCreateDTO` already accepts (`country_code` resolved to a
   * real `res.country` server-side -- WB-7137). No-ops when no real API
   * key is configured (see `googlePlacesAvailable` / `simulateAddressAutoComplete`).
   *
   * Bound to `(focus)`, which fires on every click-in -- guarded so the
   * widget (and its `place_changed` listener) is only ever created once per
   * input, instead of stacking a new one on top of the last on every focus.
   */
  addressAutoComplete(): void {
    if (!this.googlePlacesAvailable || this.autocomplete) return;

    const input = this.addressFieldRef?.nativeElement.querySelector('input');
    if (!input) {
      console.error(`Address input not found for autocomplete`);
      return;
    }

    this.autocomplete = new google.maps.places.Autocomplete(input, { language: 'en' });
    this.autocomplete.addListener('place_changed', () => {
      // Google's callback fires outside Angular's zone -- force change
      // detection so the populated fields aren't left visually stale.
      this.ngZone.run(() => {
        const place = this.autocomplete.getPlace();
        if (!place?.address_components) {
          console.error('No place found');
          return;
        }
        this.applyAddressComponents(place.address_components);
        // The Places widget overwrites the input's DOM value with its own
        // formatted string as part of selecting a suggestion, bypassing
        // Angular's [(ngModel)] binding -- put back exactly what the user
        // typed (the Address field is never changed by a search/selection).
        if (input.value !== this.apartment) input.value = this.apartment;
      });
    });
  }

  isSearching = false;
  searchError = false;
  private searchTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private static readonly SEARCH_TIMEOUT_MS = 8000;

  /**
   * Explicit "Search" button next to the Address field: geocodes whatever
   * text is currently typed (paste-friendly, no dropdown interaction
   * needed) and fills street/city/zip/province/country from the result —
   * a one-shot alternative to picking a live suggestion via
   * `addressAutoComplete()`. Uses `google.maps.Geocoder`, part of the base
   * Maps JS API (no extra library beyond what's already loaded).
   *
   * An invalid/placeholder API key (index.html not wired up yet) makes
   * Google's SDK silently refuse to send the request at all -- the
   * `geocode()` callback then never fires, so this always races a timeout
   * rather than waiting on the callback forever.
   */
  searchAddress(): void {
    this.searchError = false;
    if (!this.apartment.trim()) return;

    if (!this.googlePlacesAvailable || typeof google.maps.Geocoder === 'undefined') {
      this.simulateAddressAutoComplete();
      return;
    }

    this.isSearching = true;
    const geocoder = new google.maps.Geocoder();

    this.searchTimeoutHandle = setTimeout(() => {
      this.ngZone.run(() => {
        this.searchTimeoutHandle = null;
        this.isSearching = false;
        this.searchError = true;
        console.error(
          'Address search timed out after %dms — likely an invalid/placeholder Maps API key '
          + '(check the browser console for an InvalidKeyMapError just above this line).',
          CreateLocationFormComponent.SEARCH_TIMEOUT_MS,
        );
      });
    }, CreateLocationFormComponent.SEARCH_TIMEOUT_MS);

    geocoder.geocode({ address: this.apartment.trim() }, (results: any, status: string) => {
      // Google's callback can fire outside Angular's zone; force change
      // detection so the "Searching…" label/disabled state actually clears.
      this.ngZone.run(() => {
        if (this.searchTimeoutHandle === null) return; // timeout already fired first
        clearTimeout(this.searchTimeoutHandle);
        this.searchTimeoutHandle = null;
        this.isSearching = false;

        if (status !== 'OK' || !results?.[0]?.address_components) {
          console.error('Address search failed:', status);
          this.searchError = true;
          return;
        }
        this.applyAddressComponents(results[0].address_components);
      });
    });
  }

  /**
   * Dev-only stand-in for `addressAutoComplete()` when no real Google Maps
   * API key is configured locally (no Google Cloud account needed to
   * test the form) -- feeds the exact same `address_components` shape a
   * real Places response would return through the exact same field
   * mapping, so it exercises the real wiring, not a shortcut. Only ever
   * shown in the template while `googlePlacesAvailable` is false.
   */
  simulateAddressAutoComplete(): void {
    this.applyAddressComponents([
      { long_name: '12', short_name: '12', types: ['street_number'] },
      { long_name: 'Rue de la Paix', short_name: 'Rue de la Paix', types: ['route'] },
      { long_name: 'Paris', short_name: 'Paris', types: ['locality'] },
      { long_name: '75001', short_name: '75001', types: ['postal_code'] },
      { long_name: 'Île-de-France', short_name: 'IDF', types: ['administrative_area_level_1'] },
      { long_name: 'France', short_name: 'FR', types: ['country'] },
    ]);
  }

  private applyAddressComponents(components: Array<{ long_name: string; short_name: string; types: string[] }>): void {
    let streetNumber = '';
    let route = '';
    let city = '';
    let postalCode = '';
    let province = '';
    let countryName = '';
    let countryCode = '';

    for (const component of components) {
      const types = component.types;
      if (types.includes('street_number')) streetNumber = component.long_name;
      if (types.includes('route')) route = component.long_name;
      if (types.includes('locality')) city = component.long_name;
      if (types.includes('postal_code')) postalCode = component.long_name;
      if (types.includes('administrative_area_level_1')) province = component.long_name;
      if (types.includes('country')) {
        countryName = component.long_name;
        countryCode = component.short_name;
      }
    }

    this.street = `${streetNumber} ${route}`.trim();
    this.city = city;
    this.zip = postalCode;
    // Address keeps exactly what the user typed — never reconstructed
    // from the search result, on any of the three trigger paths.

    if (countryCode) {
      this.autoSelectCountryAndProvince(countryCode, province);
    } else {
      this.selectedCountry = null;
      this.selectedProvince = null;
      this.provinceOptions = [];
      this.provinceSearchTerm = province;
    }
  }

  /**
   * Resolves a Google-extracted country code to a real `res.country` (exact
   * ISO match, locale-independent), auto-selecting it in the Country
   * dropdown; then, if a region was also extracted, looks it up scoped to
   * that country and auto-selects it in the Province dropdown when it
   * matches a real `res.country.state` -- some countries (e.g. France in
   * base Odoo) have none at all, in which case `provinceSearchTerm` still
   * carries the parsed text as a plain-text fallback.
   */
  private autoSelectCountryAndProvince(countryCode: string, provinceName: string): void {
    this.customersService.listAllCountries(undefined, countryCode).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        const match = ((res?.data as ICountry[]) || [])[0] || null;
        this.selectedCountry = match;
        this.selectedProvince = null;
        this.provinceOptions = [];
        this.provinceSearchTerm = provinceName;

        if (match && provinceName) {
          this.customersService.listAllStates(provinceName, match.id).pipe(takeUntil(this.destroy$)).subscribe({
            next: (stateRes) => {
              const rows = (stateRes?.data as IState[]) || [];
              this.provinceOptions = rows;
              const stateMatch = rows.find((s) => s.name.toLowerCase() === provinceName.toLowerCase());
              if (stateMatch) {
                this.selectedProvince = stateMatch;
                this.provinceSearchTerm = stateMatch.name;
              }
            },
          });
        }
      },
      error: () => {
        this.selectedCountry = null;
      },
    });
  }

  // -------------------------------------------------------------------
  // Country picker (same searchable-panel pattern as the company picker)
  // -------------------------------------------------------------------

  toggleCountryPicker(): void {
    this.showCountryPicker = !this.showCountryPicker;
    if (this.showCountryPicker && this.countryOptions.length === 0) this.loadCountries('');
  }

  onCountrySearch(term: string): void {
    this.countrySearchTerm = term;
    this.countrySearch$.next(term);
  }

  selectCountry(country: ICountry): void {
    this.selectedCountry = country;
    this.showCountryPicker = false;
    // Province is scoped to the country -- a country change invalidates
    // whatever was selected/loaded for the previous one.
    this.selectedProvince = null;
    this.provinceSearchTerm = '';
    this.provinceOptions = [];
    this.loadProvinces('');
  }

  clearCountry(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedCountry = null;
    this.selectedProvince = null;
    this.provinceSearchTerm = '';
    this.provinceOptions = [];
  }

  private loadCountries(search: string): void {
    this.isLoadingCountries = true;
    this.customersService.listAllCountries(search).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.countryOptions = (res?.data as ICountry[]) || [];
        this.isLoadingCountries = false;
      },
      error: () => { this.isLoadingCountries = false; },
    });
  }

  // -------------------------------------------------------------------
  // Province/State picker -- disabled until a country is selected
  // -------------------------------------------------------------------

  toggleProvincePicker(): void {
    if (!this.selectedCountry) return;
    this.showProvincePicker = !this.showProvincePicker;
    if (this.showProvincePicker && this.provinceOptions.length === 0) this.loadProvinces('');
  }

  onProvinceSearch(term: string): void {
    this.provinceSearchTerm = term;
    this.provinceSearch$.next(term);
  }

  selectProvince(province: IState): void {
    this.selectedProvince = province;
    this.provinceSearchTerm = province.name;
    this.showProvincePicker = false;
  }

  clearProvince(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedProvince = null;
    this.provinceSearchTerm = '';
  }

  private loadProvinces(search: string): void {
    if (!this.selectedCountry) {
      this.provinceOptions = [];
      return;
    }
    this.isLoadingProvinces = true;
    this.customersService.listAllStates(search, this.selectedCountry.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.provinceOptions = (res?.data as IState[]) || [];
        this.isLoadingProvinces = false;
      },
      error: () => { this.isLoadingProvinces = false; },
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    this.closePickerIfOutside('company-select', target, () => this.showCompanyPicker, (v) => (this.showCompanyPicker = v));
    this.closePickerIfOutside('country-select', target, () => this.showCountryPicker, (v) => (this.showCountryPicker = v));
    this.closePickerIfOutside('province-select', target, () => this.showProvincePicker, (v) => (this.showProvincePicker = v));
  }

  private closePickerIfOutside(
    selectorClass: string,
    target: Node,
    isOpen: () => boolean,
    setOpen: (value: boolean) => void,
  ): void {
    if (!isOpen()) return;
    const select = this.elementRef.nativeElement.querySelector(`.${selectorClass}`);
    if (select && !select.contains(target)) setOpen(false);
  }

  private loadCompanies(search: string, append: boolean): void {
    if (append) this.isLoadingMoreCompanies = true;
    else this.isLoadingCompanies = true;

    const page: IPageCount = { number: this.companyPage, size: this.companyPageSize };
    this.customersService.listCustomers(page, undefined, search).subscribe({
      next: (res) => {
        const rows = (res?.data as ICustomer[]) || [];
        this.canLoadMoreCompanies = rows.length === this.companyPageSize;
        this.companies = append ? [...this.companies, ...rows] : rows;
        this.isLoadingCompanies = false;
        this.isLoadingMoreCompanies = false;
      },
      error: () => {
        this.isLoadingCompanies = false;
        this.isLoadingMoreCompanies = false;
      },
    });
  }
}
